import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import { AuthProvider, useAuth } from './shared/context/AuthContext'
import { TenantProvider, useTenant } from './platform/tenant/TenantContext'
import ProtectedRoute from './components/auth/ProtectedRoute'
import RestrictedRoute from './components/auth/RestrictedRoute'
import PageContainer from './components/layout/PageContainer'
import ErrorBoundary from './shared/components/ErrorBoundary'
import { useHostBranding } from './shared/hooks/useHostBranding'
import TenantChat from './modules/multichat/TenantChat'
import TenantHeader from './tenants/santaclara/landing/components/TenantHeader'
import TenantFooter from './tenants/santaclara/landing/components/TenantFooter'
import PublicSeo from './platform/seo/PublicSeo'
import { SantaClaraPrivacy, SantaClaraTerms, QoriaxPrivacy, QoriaxTerms, PublicNotFound } from './shared/pages/PublicLegalPages'

const Login = lazy(() => import('./components/auth/Login'))
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'))
const RecepcionPanel = lazy(() => import('./components/recepcion/RecepcionPanel'))
const ReceptionManage = lazy(() => import('./modules/reception/ReceptionManage'))
const SpecialistsList = lazy(() => import('./components/specialists/SpecialistsList'))
const PatientPortal = lazy(() => import('./components/patient/PatientPortal'))
const TelemedicinePanel = lazy(() => import('./modules/telemedicine/TelemedicinePanel'))
const SurgeryPanel = lazy(() => import('./modules/surgery/SurgeryPanel'))
const InpatientOperationsPanel = lazy(() => import('./modules/clinical/InpatientOperationsPanel'))
const TenantManagementPanel = lazy(() => import('./modules/tenant-management/TenantManagementPanel'))
const MultiChatPanel = lazy(() => import('./modules/multichat/MultiChatPanel'))
const SaaSPortal = lazy(() => import('./modules/saas-portal/SaaSPortal'))
const CameraControlPanel = lazy(() => import('./modules/cameras/CameraControlPanel'))
const Appointments = lazy(() => import('./modules/appointments/Appointments'))
const ClinicalWorkspace = lazy(() => import('./modules/clinical/ClinicalWorkspace'))

const QoriaxLanding = lazy(() => import('./modules/qoriax-site/QoriaxLanding'))
const Home = lazy(() => import('./tenants/santaclara/landing/components/Home'))

function isQoriaxHost() {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname.replace(/^www\./, '')
  return h === 'qoriax.com' || h === 'coriex.com' || h.startsWith('saas.qoriax') || h.includes('qoriax-main-deploy')
}
const PublicSpecialties = lazy(() => import('./tenants/santaclara/landing/components/PublicSpecialties'))
const DoctorsList = lazy(() => import('./tenants/santaclara/landing/components/DoctorsList'))
const Contact = lazy(() => import('./tenants/santaclara/landing/components/Contact'))
const AprendeSalud = lazy(() => import('./tenants/santaclara/landing/public/AprendeSalud'))
const PortalPaciente = lazy(() => import('./tenants/santaclara/landing/public/PortalPaciente'))
const MisTurnos = lazy(() => import('./tenants/santaclara/landing/public/MisTurnos'))
const GaleriaInstitucional = lazy(() => import('./tenants/santaclara/landing/public/GaleriaInstitucional').then(m => ({ default: m.GaleriaInstitucional })))
const SCv4Shell = lazy(() => import('./tenants/santaclara/v4/SCv4Shell'))
const HomeV4 = lazy(() => import('./tenants/santaclara/v4/HomeV4'))
const EquipoMedicoV4 = lazy(() => import('./tenants/santaclara/v4/EquipoMedicoV4'))
const NosotrosV4 = lazy(() => import('./tenants/santaclara/v4/NosotrosV4'))
const AgendarV4 = lazy(() => import('./tenants/santaclara/v4/AgendarV4'))
const PortalV4 = lazy(() => import('./tenants/santaclara/v4/PortalV4'))
import { SANTACLARA_PUBLIC_PORTAL_ENABLED } from './tenants/santaclara/v4/publicAvailability.js'
const GestionV4 = lazy(() => import('./tenants/santaclara/v4/GestionV4'))
const TeleconsultaV4 = lazy(() => import('./tenants/santaclara/v4/TeleconsultaV4'))
const QuirofanoV4 = lazy(() => import('./tenants/santaclara/v4/QuirofanoV4'))
const InternacionV4 = lazy(() => import('./tenants/santaclara/v4/InternacionV4'))

function LandingLayout() {
  return (
    <div className="sc-site">
      {!isQoriaxHost() && <TenantHeader />}
      <div className={!isQoriaxHost() ? 'pt-24' : ''}>
        <Outlet />
      </div>
      {!isQoriaxHost() && <TenantFooter />}
    </div>
  )
}

function AppShell() {
  const { user } = useAuth()
  const { tenantId, isModuleEnabled } = useTenant()
  const activeTenantId = user?.tenant_id || tenantId || null
  const location = useLocation()
  const showChat = location.pathname.startsWith('/app')

  return (
    <>
      <PublicSeo />
      <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'system-ui,sans-serif',color:'#555'}}>Cargando...</div>}>
        <Routes>
          {!isQoriaxHost() && <Route path="/" element={<SCv4Shell><HomeV4 /></SCv4Shell>} />}

          <Route element={<LandingLayout />}>
            {isQoriaxHost() && <Route path="/" element={<QoriaxLanding />} />}
            {isQoriaxHost() && <Route path="/qoriax" element={<QoriaxLanding />} />}
            {!isQoriaxHost() && <Route path="/especialidades" element={<Navigate to="/agendar" replace />} />}
            {!isQoriaxHost() && <Route path="/contacto" element={<Navigate to="/" replace />} />}
            {!isQoriaxHost() && <Route path="/aprende" element={<AprendeSalud />} />}
            {!isQoriaxHost() && <Route path="/mi" element={<Navigate to="/portal" replace />} />}
            {!isQoriaxHost() && <Route path="/mis-turnos" element={<Navigate to="/portal" replace />} />}
            {!isQoriaxHost() && <Route path="/galeria" element={<GaleriaInstitucional />} />}
            {!isQoriaxHost() && <Route path="/agendar" element={<Navigate to="/agenda" replace />} />}
          </Route>

          {!isQoriaxHost() && <Route path="/agenda" element={<SCv4Shell><AgendarV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/medicos" element={<SCv4Shell><EquipoMedicoV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/nosotros" element={<SCv4Shell><NosotrosV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/portal" element={SANTACLARA_PUBLIC_PORTAL_ENABLED ? <SCv4Shell><PortalV4 /></SCv4Shell> : <Navigate to="/agenda" replace />} />}
          {!isQoriaxHost() && <Route path="/gestion" element={<SCv4Shell><GestionV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/teleconsulta/:id" element={<SCv4Shell><TeleconsultaV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/quirofano" element={<SCv4Shell><QuirofanoV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/internacion" element={<SCv4Shell><InternacionV4 /></SCv4Shell>} />}
          {!isQoriaxHost() && <Route path="/agenda-legacy" element={<Navigate to="/agenda" replace />} />}

          {!isQoriaxHost() && <Route path="/privacidad" element={<SantaClaraPrivacy />} />}
          {!isQoriaxHost() && <Route path="/privacy" element={<SantaClaraPrivacy />} />}
          {!isQoriaxHost() && <Route path="/terminos" element={<SantaClaraTerms />} />}
          {!isQoriaxHost() && <Route path="/terms" element={<SantaClaraTerms />} />}
          {isQoriaxHost() && <Route path="/privacidad" element={<QoriaxPrivacy />} />}
          {isQoriaxHost() && <Route path="/privacy" element={<QoriaxPrivacy />} />}
          {isQoriaxHost() && <Route path="/terminos" element={<QoriaxTerms />} />}
          {isQoriaxHost() && <Route path="/terms" element={<QoriaxTerms />} />}

          {isQoriaxHost() && <Route path="/saas" element={<SaaSPortal />} />}

          {isQoriaxHost()
            ? <Route path="/login" element={<Login />} />
            : <Route path="/login" element={<Navigate to="/gestion" replace />} />}
          {!isQoriaxHost() && <Route path="/patient-portal" element={<PatientPortal />} />}

          {isQoriaxHost() && (
            <Route path="/app" element={<ProtectedRoute><PageContainer /></ProtectedRoute>}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="recepcion" element={<RecepcionPanel />} />
              <Route path="gestion" element={<ReceptionManage tenantId={activeTenantId} />} />
              <Route path="appointments" element={<Appointments tenantId={activeTenantId} />} />
              <Route path="specialists" element={<SpecialistsList />} />
              <Route path="telemedicine" element={<RestrictedRoute moduleId="telemedicine" user={user}><TelemedicinePanel /></RestrictedRoute>} />
              <Route path="surgery" element={<RestrictedRoute moduleId="surgery" user={user}><SurgeryPanel /></RestrictedRoute>} />
              <Route path="inpatient" element={<RestrictedRoute moduleId="inpatient" user={user}><InpatientOperationsPanel tenantId={activeTenantId} /></RestrictedRoute>} />
              <Route path="inpatient/icu-adult" element={<RestrictedRoute moduleId="icu_adult" user={user}><InpatientOperationsPanel tenantId={activeTenantId} /></RestrictedRoute>} />
              <Route path="inpatient/icu-neoped" element={<RestrictedRoute moduleId="icu_neoped" user={user}><InpatientOperationsPanel tenantId={activeTenantId} /></RestrictedRoute>} />
              <Route path="cameras" element={<RestrictedRoute moduleId="cameras" user={user}><CameraControlPanel tenantId={activeTenantId} /></RestrictedRoute>} />
              <Route path="tenant-management" element={<RestrictedRoute moduleId="tenant_management" user={user}><TenantManagementPanel /></RestrictedRoute>} />
              <Route path="multichat" element={<MultiChatPanel tenantId={activeTenantId} />} />
              <Route path="clinical" element={<RestrictedRoute moduleId="clinical_workspace" user={user}><ClinicalWorkspace tenantId={activeTenantId} /></RestrictedRoute>} />
            </Route>
          )}
          {!isQoriaxHost() && <Route path="/app" element={<Navigate to="/gestion" replace />} />}
          {!isQoriaxHost() && <Route path="/app/gestion" element={<Navigate to="/gestion" replace />} />}

          <Route path="*" element={<PublicNotFound />} />
        </Routes>
      </Suspense>
      {activeTenantId && isModuleEnabled('multichat') && showChat && <TenantChat tenantId={activeTenantId} />}
    </>
  )
}

function App() {
  useHostBranding()
  return (
    <ErrorBoundary>
      <TenantProvider>
        <AuthProvider>
          <Router>
            <AppShell />
          </Router>
        </AuthProvider>
      </TenantProvider>
    </ErrorBoundary>
  )
}

export default App